import type { Transfer } from '@interfaces/transfer';

import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Box, IconButton, Typography } from 'hephaestus/web';
import { CloseRounded as CloseRoundedIcon } from 'hephaestus/webIcons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BankAccountTransfer } from './bankAccountTransfer.dialogContent';

type BankAccountTransferReviewItemProps = {
  transfer: Transfer | BankAccountTransfer;
  onDeleteTransfer: () => void;
};
const BankAccountTransferReviewItem = ({
  transfer,
  onDeleteTransfer,
}: BankAccountTransferReviewItemProps) => {
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);
  const defineTitle = (): string =>
    t('workflowCompletions:bankAccountTransferReviewPanel.title', {
      amount: `$${(transfer.amountCents / 100).toLocaleString('en-US')}`,
      accountType: t(
        `accountTypes:${transfer?.subAccount?.account?.type ?? ''}`,
      ),
    });
  const getBankName = (): string => transfer.bankAccount?.name ?? '';
  const getInstitutionNumber = (): string =>
    transfer.bankAccount?.institutionNumber ?? '';
  const getTransitNumber = (): string =>
    transfer.bankAccount?.transitNumberDisclosed ??
    transfer.bankAccount?.transitNumber ??
    '';
  const getBankAccountNumber = (): string =>
    transfer.bankAccount?.bankAccountNumberDisclosed ??
    transfer.bankAccount?.bankAccountNumber ??
    '';
  const getFrequency = (): string => {
    if ('frequency' in transfer) {
      return t(`client:frequency.${transfer.frequency}`);
    }
    return '';
  };
  const isRecurring = () => {
    if ('isRecurring' in transfer) return transfer.isRecurring;
    return false;
  };
  const getIsRecurring = (): string => (isRecurring() ? 'Yes' : 'No');
  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            pl: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodyLarge' sx={{ mt: 1, fontWeight: '600' }}>
            {defineTitle()}
          </Typography>
          {'id' in transfer && transfer.id ? undefined : (
            <IconButton aria-label='close' onClick={() => onDeleteTransfer()}>
              <Icon icon={CloseRoundedIcon} size='small' />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t('workflowCompletions:bankAccountTransferReviewPanel.type')}
          </Typography>
          <Typography variant='labelLarge'>
            {t('workflowCompletions:bankAccountTransferReviewPanel.deposit')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t(
              'workflowCompletions:bankAccountTransferReviewPanel.bankAccount',
            )}
          </Typography>
          <Typography variant='labelLarge'>{getBankName()}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t(
              'workflowCompletions:bankAccountTransferReviewPanel.institutionNumber',
            )}
          </Typography>
          <Typography variant='labelLarge'>{getInstitutionNumber()}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t(
              'workflowCompletions:bankAccountTransferReviewPanel.transitNumber',
            )}
          </Typography>
          <Typography variant='labelLarge'>{getTransitNumber()}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t(
              'workflowCompletions:bankAccountTransferReviewPanel.accountNumber',
            )}
          </Typography>
          <Typography variant='labelLarge'>{getBankAccountNumber()}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: 2,
            mb: 1,
            px: '8px',
          }}
        >
          <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
            {t('workflowCompletions:bankAccountTransferReviewPanel.recurring')}
          </Typography>
          <Typography variant='labelLarge'>{getIsRecurring()}</Typography>
        </Box>
        {isRecurring() ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              mt: 2,
              mb: 1,
              px: '8px',
            }}
          >
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {t(
                'workflowCompletions:bankAccountTransferReviewPanel.frequency',
              )}
            </Typography>
            <Typography variant='labelLarge'>{getFrequency()}</Typography>
          </Box>
        ) : undefined}
        {isRecurring() ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              mt: 2,
              mb: 1,
              px: '8px',
            }}
          >
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {t(
                'workflowCompletions:bankAccountTransferReviewPanel.startDate',
              )}
            </Typography>
            <Typography variant='labelLarge'>
              {transfer.scheduledDate?.toString() ?? ''}
            </Typography>
          </Box>
        ) : undefined}
      </CardContent>
    </Card>
  );
};

export default BankAccountTransferReviewItem;
